import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import Layout from '../layout/Layout';
import { bookingApi, roomApi, climateApi } from '../../services/api';
import { parseRRuleToHumanReadable, getNextOccurrence } from '../../utils/recurrenceParser';
import wsService from '../../services/websocket';
import './EventSimple.css';

// M004-F0075: Generate start time options in 15-minute intervals (6am to 10pm)
const generateTimeOptions = () => {
  const options = [];
  // Start at 6am (6), end at 10pm (22) to allow at least 1hr booking before 11pm cutoff
  for (let hour = 6; hour <= 22; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      const displayHour = hour > 12 ? hour - 12 : hour;
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayTime = `${displayHour}:${minute.toString().padStart(2, '0')} ${ampm}`;
      options.push({ value: time, label: displayTime });
    }
  }
  return options;
};

// M004-F0076: Generate end time options - must be after start time, in 1-hour increments, max 11pm
const generateEndTimeOptions = (startTime) => {
  if (!startTime) return [];
  
  const [startHour, startMin] = startTime.split(':').map(Number);
  const startTotalMinutes = startHour * 60 + startMin;
  const maxEndHour = 23; // 11pm max
  const options = [];
  
  // Generate options in 1-hour increments from start time
  for (let hours = 1; hours <= 6; hours++) {
    const endTotalMinutes = startTotalMinutes + (hours * 60);
    const endHour = Math.floor(endTotalMinutes / 60);
    const endMin = endTotalMinutes % 60;
    
    // Don't go past 11pm (23:00)
    if (endHour > maxEndHour) break;
    
    const timeStr = `${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`;
    const displayHour = endHour > 12 ? endHour - 12 : endHour;
    const ampm = endHour >= 12 ? 'PM' : 'AM';
    const displayStr = `${displayHour}:${endMin.toString().padStart(2, '0')} ${ampm} (${hours}hr)`;
    options.push({ value: timeStr, label: displayStr });
  }
  
  return options;
};

const TIME_OPTIONS = generateTimeOptions();

// M007-F010 / Defect C: a booking is part of a recurring series if it is
// either the master ('recurring') or a materialised instance
// ('recurring_instance'), or carries any of the legacy recurrence markers.
// Used to keep the "recurring series" notice and the "locked" notice
// mutually exclusive (a series is never individually lockable).
export const isRecurringSeries = (booking) => Boolean(
  booking && (
    booking.booking_type === 'recurring'
    || booking.booking_type === 'recurring_instance'
    || booking.google_recurring_event_id
    || booking.recurring?.isRecurring
  ),
);

// Defect A: for a recurring master booking, the card must show the series'
// next occurrence (or, once the series has ended, its last occurrence) —
// never the raw anchor (DTSTART) date. Non-recurring bookings, and
// recurring rows we cannot compute an occurrence for (no RRULE), just show
// their own start/end untouched.
export const getDisplayOccurrence = (booking, now = new Date()) => {
  if (booking?.booking_type === 'recurring' && booking.google_recurrence_rule) {
    return getNextOccurrence(booking.google_recurrence_rule, booking.start_time, booking.end_time, now);
  }
  const start = new Date(booking?.start_time);
  const end = booking?.end_time ? new Date(booking.end_time) : null;
  return {
    start,
    end,
    isEnded: end ? end.getTime() < now.getTime() : start.getTime() < now.getTime(),
    isSeries: false,
  };
};

// Defect B (scoped 2026-09-04 per independent audit of !302): `status` and
// `approval_status` can disagree in production (booking 3218:
// status='approved', approval_status='pending'). The override below exists
// to correct exactly that shape of lie — a falsely confident 'approved'
// claim — NOT as a general "approval_status always wins" rule. Every other
// value of `status` (cancelled, rejected, delete_pending, ...) is the
// booking's real, already-decided disposition; a merely-lagging
// approval_status must never override it, or a dead booking (e.g.
// status='cancelled') would render as "⏳ Waiting for Approval", presenting
// it as still awaiting review. So the override only fires when `status`
// itself claims 'approved'.
export const resolveDisplayStatus = (booking) => {
  const status = booking?.status;
  const approvalStatus = booking?.approval_status;
  const statusClaimsApproved = status === 'approved';
  if (statusClaimsApproved && approvalStatus && approvalStatus !== status
      && (approvalStatus === 'pending' || approvalStatus === 'rejected')) {
    return { key: approvalStatus, disagreement: true, status, approvalStatus };
  }
  return { key: status, disagreement: false, status, approvalStatus };
};

// M004-F0074 / Defect C: Check if booking is locked (within 20 minutes of
// start). Bounded to the booking's own window: once its end_time has
// passed the booking is simply historical, not permanently "locked" — the
// previous `minutesUntilStart > -Infinity` lower bound was always true for
// any past start, so a recurring series anchored months ago never unlocked.
export const isBookingLocked = (booking, now = new Date()) => {
  if (!booking) return false;
  const bookingStart = new Date(booking.start_time);
  const bookingEnd = booking.end_time ? new Date(booking.end_time) : bookingStart;
  const minutesUntilStart = (bookingStart.getTime() - now.getTime()) / (1000 * 60);
  const hasEnded = !Number.isNaN(bookingEnd.getTime()) && now.getTime() > bookingEnd.getTime();
  return minutesUntilStart < 20 && !hasEnded;
};

// Defect B: takes the whole booking (not just booking.status) so it can
// honour resolveDisplayStatus's disagreement rule.
// M005-F0013 A1 / M009 (T-MYBOOK-001..006): a booking is CURRENT when
//   series_end_time >= now
//   OR (series_end_time IS NULL AND google_recurrence_rule IS NOT NULL)
//   OR (series_end_time IS NULL AND google_recurrence_rule IS NULL AND start_time >= now)
// — the backend's future_only predicate, branch for branch. A recurring row
// whose series_end_time is not (yet) known — an infinite RRULE, or one not
// yet backfilled — is ACTIVE, never "past" because of its anchor start_time
// (WEB-EVS-013; production booking 3219 was hidden by exactly that).
export const isBookingCurrent = (booking, now = new Date().getTime()) => {
  const nowMs = now instanceof Date ? now.getTime() : now;
  if (booking?.series_end_time) {
    return new Date(booking.series_end_time).getTime() >= nowMs;
  }
  if (booking?.google_recurrence_rule) return true;
  return new Date(booking?.start_time).getTime() >= nowMs;
};

export const getStatusBadgeClass = (booking) => {
  const resolved = resolveDisplayStatus(booking);
  switch (resolved.key) {
    case 'approved': return 'status-badge success';
    case 'pending': return 'status-badge warning';
    case 'rejected': return 'status-badge error';
    case 'cancelled': return 'status-badge muted';
    default: return 'status-badge';
  }
};

// M004-F0074 / Defect B: Get status display text. When approval_status
// disagrees with status (e.g. status='approved', approval_status='pending'),
// resolveDisplayStatus resolves to approval_status so we never print a
// confident "Approved" badge over a row the database still considers
// pending or rejected.
export const getStatusText = (booking) => {
  const resolved = resolveDisplayStatus(booking);
  switch (resolved.key) {
    case 'approved': return '✓ Approved';
    case 'pending': return '⏳ Waiting for Approval';
    case 'rejected': return '✗ Rejected';
    case 'delete_pending': return '⏱️ Delete Pending';
    default: return resolved.key;
  }
};

const EventSimpleDashboard = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [activeBooking, setActiveBooking] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [climateData, setClimateData] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    roomId: '',
    date: '',
    startTime: '',
    endTime: '',
    purpose: ''
  });
  const [formError, setFormError] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);

  // M005-F0013 A1: which tab of the My Bookings list is open
  const [activeTab, setActiveTab] = useState('current');

  // M005-F0013 A1: split the fetched list into the Current and Past tabs,
  // with exactly the backend's future_only activity window (studioaeye-Core
  // booking.service.ts listBookings; spec M009-Event-Simple-User-Type.md,
  // T-MYBOOK-001..006). Computed at render only; the fetch is untouched.
  const { currentBookings, pastBookings } = useMemo(() => {
    const now = new Date().getTime();
    const seriesEndOf = (booking) => new Date(
      booking.series_end_time || booking.start_time
    ).getTime();
    const currentBookings = [];
    const pastBookings = [];
    bookings.forEach(booking => {
      if (isBookingCurrent(booking, now)) {
        currentBookings.push(booking);
      } else {
        pastBookings.push(booking);
      }
    });
    // Past tab: newest first by end datetime.
    pastBookings.sort((a, b) => seriesEndOf(b) - seriesEndOf(a));
    return { currentBookings, pastBookings };
  }, [bookings]);

  // Fetch bookings
  const fetchBookings = useCallback(async () => {
    try {
      // WEB-EVS-019 (ticket #259): Core's GET /bookings orders start_time ASC
      // and caps at `limit`, so one unwindowed request for an org with more
      // than 100 past rows never returns the upcoming ones. The Current tab
      // therefore asks for rows still active now: `start` (inclusive; Core
      // keeps rows whose active range ends after start, incl. open-ended
      // series). The Past tab keeps its own request without `start`, exactly
      // as before; its oldest-100 cap is ticket #261, not fixed here.
      // Results are merged by id and split client-side by isBookingCurrent.
      const unwrapList = (res) => res.data?.data || res.data || [];
      const [currentRes, allRes] = await Promise.all([
        bookingApi.getMyBookings({ start: new Date().toISOString(), limit: 100 }),
        bookingApi.getMyBookings({ limit: 100 }),
      ]);
      const byId = new Map();
      [...unwrapList(currentRes), ...unwrapList(allRes)].forEach(b => byId.set(b.id, b));
      const bookingList = [...byId.values()];
      
      // Debug: Log booking_type field for recurring detection
      console.log('Bookings loaded:', bookingList.map(b => ({
        id: b.id,
        title: b.title,
        booking_type: b.booking_type,
        google_recurring_event_id: b.google_recurring_event_id
      })));
      
      setBookings(bookingList);
      setError(null); // Clear any previous error
      
      // M004-F0069: Check for active booking (20 min before to 10 min after)
      const now = new Date();
      const MINUTES_BEFORE = 20;
      const MINUTES_AFTER = 10;
      
      const active = bookingList.find(booking => {
        if (booking.status !== 'approved') return false;
        const start = new Date(booking.start_time);
        const end = new Date(booking.end_time);
        
        // Calculate the climate control window
        const windowStart = new Date(start.getTime() - MINUTES_BEFORE * 60 * 1000);
        const windowEnd = new Date(end.getTime() + MINUTES_AFTER * 60 * 1000);
        
        return now >= windowStart && now <= windowEnd;
      });
      
      setActiveBooking(active || null);
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
      // M007-F010: Provide helpful error message with refresh suggestion
      const errorMsg = err.response?.status === 401 
        ? 'Session expired. Please refresh the page or log in again.'
        : 'Failed to load bookings. Please try refreshing the page.';
      setError(errorMsg);
    }
  }, []);

  // Fetch rooms
  const fetchRooms = useCallback(async () => {
    try {
      const response = await roomApi.getRooms();
      setRooms(response.data?.data || response.data || []);
    } catch (err) {
      console.error('Failed to fetch rooms:', err);
    }
  }, []);

  // Fetch climate data for active booking
  const fetchClimateData = useCallback(async () => {
    if (!activeBooking) {
      setClimateData(null);
      return;
    }

    try {
      const response = await climateApi.getRoomClimate(activeBooking.room_id);
      const data = response.data?.data || response.data;
      
      // Transform the API response to the expected format
      if (data && data.configured) {
        const climateState = data.climate?.attributes || {};
        const occupancyState = data.occupancy?.state?.toLowerCase() || 'unused';
        
        setClimateData({
          currentTemp: climateState.current_temperature || 0,
          targetTemp: climateState.temperature || 20,
          occupancyState: occupancyState === 'inuse' || occupancyState === 'in_use' ? 'in_use' : 'unused',
          lastUpdated: data.climate?.last_updated || new Date().toISOString(),
          hvacMode: data.climate?.state || 'off',
          configured: true
        });
      } else {
        // Room not configured for climate control
        setClimateData({
          configured: false,
          message: 'Climate control not configured for this room'
        });
      }
    } catch (err) {
      console.error('Failed to fetch climate data:', err);
      setClimateData(null);
    }
  }, [activeBooking]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchBookings(), fetchRooms()]);
      setLoading(false);
    };
    loadData();
  }, [fetchBookings, fetchRooms]);

  // M004-F0078: Subscribe to WebSocket events for real-time updates
  useEffect(() => {
    // Connect to WebSocket
    wsService.connect();

    // Subscribe to booking events
    const unsubCreated = wsService.subscribe('booking:created', (data) => {
      console.log('Booking created via WebSocket:', data);
      fetchBookings();
    });

    const unsubApproved = wsService.subscribe('booking:approved', (data) => {
      console.log('Booking approved via WebSocket:', data);
      fetchBookings();
    });

    const unsubRejected = wsService.subscribe('booking:rejected', (data) => {
      console.log('Booking rejected via WebSocket:', data);
      fetchBookings();
    });

    const unsubUpdated = wsService.subscribe('booking:updated', (data) => {
      console.log('Booking updated via WebSocket:', data);
      fetchBookings();
    });

    const unsubDeleted = wsService.subscribe('booking:deleted', (data) => {
      console.log('Booking deleted via WebSocket:', data);
      fetchBookings();
    });

    // Cleanup subscriptions on unmount
    return () => {
      unsubCreated();
      unsubApproved();
      unsubRejected();
      unsubUpdated();
      unsubDeleted();
    };
  }, [fetchBookings]);

  // M003-F017: Subscribe to climate and occupancy WebSocket events for real-time updates
  useEffect(() => {
    if (!activeBooking) return;

    // Subscribe to the room for climate/occupancy updates
    wsService.subscribeToRoom(activeBooking.room_id);
    console.log('Subscribed to room for climate updates:', activeBooking.room_id);

    // Handle climate updates from Home Assistant webhook
    const unsubClimate = wsService.subscribe('climate', (data) => {
      console.log('Climate update via WebSocket:', data);
      // Only update if it's for our room
      if (data.roomId === activeBooking.room_id) {
        const climateState = data.climate || {};
        setClimateData(prev => ({
          ...prev,
          currentTemp: climateState.current_temperature || prev?.currentTemp || 0,
          targetTemp: climateState.target_temperature || prev?.targetTemp || 20,
          hvacMode: climateState.hvac_mode || climateState.state || prev?.hvacMode || 'off',
          lastUpdated: climateState.timestamp || new Date().toISOString(),
          configured: true
        }));
      }
    });

    // Handle occupancy updates from Home Assistant webhook
    const unsubOccupancy = wsService.subscribe('occupancy', (data) => {
      console.log('Occupancy update via WebSocket:', data);
      // Only update if it's for our room
      if (data.roomId === activeBooking.room_id) {
        const occupancyState = data.occupancy?.state?.toLowerCase() || 'unused';
        setClimateData(prev => ({
          ...prev,
          occupancyState: occupancyState === 'inuse' || occupancyState === 'in_use' ? 'in_use' : 'unused',
          lastUpdated: data.occupancy?.timestamp || new Date().toISOString()
        }));
      }
    });

    return () => {
      unsubClimate();
      unsubOccupancy();
    };
  }, [activeBooking]);

  // Poll climate data when active booking exists (fallback if WebSocket not available)
  useEffect(() => {
    if (!activeBooking) return;

    fetchClimateData();
    const interval = setInterval(fetchClimateData, 30000); // Poll every 30 seconds as fallback

    return () => clearInterval(interval);
  }, [activeBooking, fetchClimateData]);

  // M004-F0074: Handle delete booking with lifecycle support
  const handleDeleteBooking = async (bookingId, roomName, status) => {
    const isApproved = status === 'approved';
    const confirmMessage = isApproved 
      ? `Request to delete booking for ${roomName}? Admin approval required.`
      : `Delete booking for ${roomName}?`;
    
    if (!window.confirm(confirmMessage)) return;

    try {
      const response = await bookingApi.deleteBooking(bookingId);
      await fetchBookings();
      
      // M004-F0074-E: Handle delete_pending response
      if (response.data?.action === 'delete_pending') {
        alert('Deletion request submitted for admin approval.');
      }
    } catch (err) {
      alert('Failed to delete booking: ' + (err.response?.data?.message || err.message));
    }
  };

  // M004-F0074-F: Handle undo delete request
  const handleUndoDelete = async (bookingId) => {
    if (!window.confirm('Cancel your deletion request?')) return;

    try {
      await bookingApi.undoDeleteRequest(bookingId);
      await fetchBookings();
    } catch (err) {
      alert('Failed to cancel deletion: ' + (err.response?.data?.message || err.message));
    }
  };

  // M009-F011: Check if booking can be deleted (not recurring, correct status, not locked)
  const canDeleteBooking = (booking) => {
    // Recurring bookings cannot be deleted by event_simple users
    if (isRecurringSeries(booking)) return false;
    // Only approved or pending bookings can be deleted
    if (!['approved', 'pending'].includes(booking.status)) return false;
    // Locked bookings cannot be deleted
    if (isBookingLocked(booking)) return false;
    return true;
  };

  // M004-F0076: Handle form input change - auto-set endTime when startTime changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'startTime') {
      // Auto-set endTime to 1 hour after startTime (default duration)
      const [startHour, startMin] = value.split(':').map(Number);
      const endTotalMinutes = (startHour * 60 + startMin) + 60; // Add 1 hour
      const endHour = Math.floor(endTotalMinutes / 60);
      const endMin = endTotalMinutes % 60;
      
      // Only set if within valid range (max 11pm)
      let newEndTime = '';
      if (endHour <= 23) {
        newEndTime = `${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`;
      }
      
      setFormData(prev => ({ ...prev, [name]: value, endTime: newEndTime }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    setFormError('');
  };

  // M004-F0076: Generate end time options based on selected start time
  const endTimeOptions = generateEndTimeOptions(formData.startTime);

  // Validate booking form
  const validateBooking = () => {
    if (!formData.roomId) return 'Please select a room';
    if (!formData.date) return 'Please select a date';
    if (!formData.startTime) return 'Please select start time';
    if (!formData.endTime) return 'Please select end time';
    
    // Validate 15-minute intervals
    const startMinutes = parseInt(formData.startTime.split(':')[1], 10);
    const endMinutes = parseInt(formData.endTime.split(':')[1], 10);
    if (startMinutes % 15 !== 0) {
      return 'Start time must be on a 15-minute interval (00, 15, 30, or 45)';
    }
    if (endMinutes % 15 !== 0) {
      return 'End time must be on a 15-minute interval (00, 15, 30, or 45)';
    }
    
    const selectedDate = new Date(formData.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      return 'Cannot book past dates';
    }
    
    if (formData.endTime <= formData.startTime) {
      return 'End time must be after start time';
    }
    
    // Calculate duration in minutes
    const start = new Date(`2000-01-01 ${formData.startTime}`);
    const end = new Date(`2000-01-01 ${formData.endTime}`);
    const duration = (end - start) / (1000 * 60);
    
    if (duration < 30) {
      return 'Minimum booking duration is 30 minutes';
    }
    
    if (duration > 480) {
      return 'Maximum booking duration is 8 hours';
    }
    
    return null;
  };

  // Handle create booking
  const handleCreateBooking = async (e) => {
    e.preventDefault();
    
    const validationError = validateBooking();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setFormSubmitting(true);
    setFormError('');

    try {
      // Combine date with time for API (backend expects snake_case)
      const startDateTime = `${formData.date}T${formData.startTime}:00`;
      const endDateTime = `${formData.date}T${formData.endTime}:00`;
      
      await bookingApi.createBooking({
        room_id: parseInt(formData.roomId),
        title: formData.purpose || 'Event Booking',
        description: formData.purpose,
        start_time: startDateTime,
        end_time: endDateTime,
        booking_type: 'one-off'
      });

      // Reset form
      setFormData({
        roomId: '',
        date: '',
        startTime: '',
        endTime: '',
        purpose: ''
      });

      // Refresh bookings
      await fetchBookings();
      alert('Booking created successfully and awaiting admin approval');
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to create booking');
    } finally {
      setFormSubmitting(false);
    }
  };

  // Handle climate control changes
  const handleTemperatureChange = async (newTemp) => {
    if (!activeBooking) return;

    try {
      await climateApi.setTemperature(activeBooking.room_id, newTemp);
      await fetchClimateData();
    } catch (err) {
      alert('Failed to update temperature');
    }
  };

  const handleOccupancyChange = async (newState) => {
    if (!activeBooking) return;

    try {
      await climateApi.setOccupancy(activeBooking.room_id, newState);
      await fetchClimateData();
    } catch (err) {
      alert('Failed to update occupancy');
    }
  };

  // Format date/time for display
  const formatDateTime = (dateTime) => {
    return new Date(dateTime).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // getStatusBadgeClass / getStatusText (Defect B) live at module scope
  // below, exported alongside resolveDisplayStatus for direct unit testing.

  if (loading) {
    return (
      <Layout>
        <div className="event-simple-container">
          <div className="loading-indicator">
            <div className="spinner"></div>
            <p>Loading...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="event-simple-container">
        <header className="page-header">
          <div>
            <h1>My Bookings</h1>
            <p className="subtitle">Welcome, {user?.displayName}</p>
          </div>
        </header>

        {error && (
          <div className="alert alert-error">
            {error}
            <button 
              className="btn btn-sm btn-outline" 
              style={{ marginLeft: '10px' }}
              onClick={() => window.location.reload()}
            >
              🔄 Refresh Page
            </button>
          </div>
        )}

        {/* Active Climate Control - shown at top when booking is active */}
        {activeBooking && (
          <section className="climate-section">
            <h2>🌡️ Active Climate Control</h2>
            <div className="climate-card">
              <div className="climate-header">
                <h3>{activeBooking.room_name || `Room ${activeBooking.room_id}`}</h3>
                <span className="status-badge success">Currently Active</span>
              </div>
              
              {climateData && climateData.configured ? (
                <>
                  <div className="climate-control">
                    <label>Temperature: {climateData.targetTemp}°C</label>
                    <input
                      type="range"
                      min="16"
                      max="30"
                      value={climateData.targetTemp}
                      onChange={(e) => handleTemperatureChange(parseInt(e.target.value))}
                      className="temperature-slider"
                    />
                    <div className="temp-range">
                      <span>16°C</span>
                      <span>30°C</span>
                    </div>
                  </div>

                  <div className="occupancy-control">
                    <label>Occupancy:</label>
                    <div className="toggle-buttons">
                      <button
                        className={`btn btn-sm ${climateData.occupancyState === 'in_use' ? 'btn-primary' : 'btn-outline'}`}
                        onClick={() => handleOccupancyChange('in_use')}
                      >
                        ● In Use
                      </button>
                      <button
                        className={`btn btn-sm ${climateData.occupancyState === 'unused' ? 'btn-primary' : 'btn-outline'}`}
                        onClick={() => handleOccupancyChange('unused')}
                      >
                        ○ Unused
                      </button>
                    </div>
                  </div>

                  <div className="climate-info">
                    <small>Current: {climateData.currentTemp}°C</small>
                    <small>Last updated: {new Date(climateData.lastUpdated).toLocaleTimeString()}</small>
                  </div>
                </>
              ) : climateData && !climateData.configured ? (
                <p className="climate-not-configured">Climate control is not configured for this room.</p>
              ) : (
                <p>Loading climate data...</p>
              )}
            </div>
          </section>
        )}

        {/* Bookings List */}
        <section className="bookings-section">
          <h2>📅 My Bookings</h2>
          {bookings.length === 0 ? (
            <div className="empty-state">
              <p>No bookings yet. Create your first booking below!</p>
            </div>
          ) : (
            <>
              {/* M005-F0013 A1: the only new chrome — Current/Past tab bar.
                  The Current tab keeps the page's existing list behaviour
                  and interactions exactly. */}
              <div className="bookings-tabs" role="tablist" aria-label="My Bookings">
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'current'}
                  className={`bookings-tab${activeTab === 'current' ? ' active' : ''}`}
                  onClick={() => setActiveTab('current')}
                >
                  Current bookings
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'past'}
                  className={`bookings-tab${activeTab === 'past' ? ' active' : ''}`}
                  onClick={() => setActiveTab('past')}
                >
                  Past bookings ({pastBookings.length})
                </button>
              </div>
              {activeTab === 'current' && (
                currentBookings.length === 0 ? (
                  <div className="empty-state">
                    <p>No current bookings</p>
                  </div>
                ) : (
            <div className="bookings-list">
              {currentBookings.map(booking => {
                const recurringSeries = isRecurringSeries(booking);
                // Defect A: show the series' next (or, once ended, last) occurrence
                // instead of the raw anchor date.
                const occurrence = getDisplayOccurrence(booking);
                return (
                <div key={booking.id} className="booking-card" data-booking-id={booking.id}>
                  <div className="booking-info">
                    <h3>{booking.room_name || `Room ${booking.room_id}`}</h3>
                    <p className="booking-time">
                      {formatDateTime(occurrence.start)} - {occurrence.end ? formatDateTime(occurrence.end) : formatDateTime(booking.end_time)}
                      {occurrence.isSeries && (
                        <span className="booking-occurrence-label" data-testid="booking-occurrence-label">
                          {occurrence.isEnded ? ' (last occurrence — series ended)' : ' (next occurrence)'}
                        </span>
                      )}
                    </p>
                    {booking.purpose && <p className="booking-purpose">{booking.purpose}</p>}
                    <span className={getStatusBadgeClass(booking)}>
                      {getStatusText(booking)}
                    </span>
                    {/* Show rejection reason if booking was rejected */}
                    {booking.status === 'rejected' && booking.rejection_reason && (
                      <p className="rejection-reason">
                        <strong>Reason:</strong> {booking.rejection_reason}
                      </p>
                    )}
                    {/* M007-F010: Show recurring booking indicator */}
                    {/* Check both new fields (booking_type) and legacy fields (recurring.isRecurring) */}
                    {recurringSeries && (
                      <div className="recurring-info">
                        <span className="recurring-badge">
                          🔁 Recurring: {booking.google_recurrence_rule ? parseRRuleToHumanReadable(booking.google_recurrence_rule) : (booking.recurring?.recurrencePattern || 'Series booking')}
                        </span>
                        {booking.booking_type === 'recurring' && (
                          <span className="recurring-span" data-testid="booking-series-span">
                            Series started {formatDateTime(booking.start_time)}
                          </span>
                        )}
                        <p className="recurring-notice">
                          <em>ℹ️ Part of a recurring series - Contact admin to modify</em>
                        </p>
                      </div>
                    )}
                    {/* M004-F0074 / Defect C: Show locked notice if applicable.
                        Never alongside the recurring notice above — a series is
                        never individually lockable. */}
                    {booking.status === 'approved' && isBookingLocked(booking) && !recurringSeries && (
                      <span className="booking-locked-notice">🔒 Locked</span>
                    )}
                    {/* M009-F012: Surface failed calendar sync instead of hiding it */}
                    {booking.status === 'approved' && booking.sync_status === 'error' && (
                      <p className="sync-error-notice" data-testid="booking-sync-error">
                        ⚠️ Calendar sync failed — this booking is not yet in the room's Google Calendar. An admin can re-trigger the sync.
                      </p>
                    )}
                  </div>
                  {/* M004-F0074-F: Undo Delete button for delete_pending */}
                  {booking.status === 'delete_pending' ? (
                    <button
                      className="btn btn-sm btn-outline"
                      onClick={() => handleUndoDelete(booking.id)}
                    >
                      ↩️ Undo Delete
                    </button>
                  ) : (
                    /* M009-F011: Delete button - only show if canDeleteBooking returns true */
                    canDeleteBooking(booking) && (
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteBooking(booking.id, booking.room_name, booking.status)}
                      >
                        🗑️ Delete
                      </button>
                    )
                  )}
                </div>
                );
              })}
            </div>
                )
              )}
              {/* M005-F0013 A1: Past tab — read-only rows, newest first.
                  No cancel/undo/climate/create affordances. */}
              {activeTab === 'past' && (
                pastBookings.length === 0 ? (
                  <div className="empty-state">
                    <p>No past bookings</p>
                  </div>
                ) : (
                  <div className="bookings-list">
                    {pastBookings.map(booking => (
                      <div key={booking.id} className="booking-card booking-card-past" data-booking-id={booking.id}>
                        <div className="booking-info">
                          <h3>{booking.room_name || `Room ${booking.room_id}`}</h3>
                          <p className="booking-time">
                            {formatDateTime(booking.start_time)} - {formatDateTime(booking.end_time)}
                          </p>
                          <span className={getStatusBadgeClass(booking)}>
                            {getStatusText(booking)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </>
          )}
        </section>

        {/* Create Booking Form */}
        <section className="booking-form-section">
          <h2>➕ Book a Room</h2>
          <form onSubmit={handleCreateBooking} className="booking-form">
            {formError && <div className="alert alert-error">{formError}</div>}

            <div className="form-group">
              <label htmlFor="roomId">Room *</label>
              <select
                id="roomId"
                name="roomId"
                value={formData.roomId}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Room</option>
                {rooms.map(room => (
                  <option key={room.id} value={room.id}>
                    {room.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="date">Date *</label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="startTime">Start Time *</label>
                <select
                  id="startTime"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select start time</option>
                  {TIME_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* M004-F0076: End time in 1-hour increments */}
              <div className="form-group">
                <label htmlFor="endTime">End Time * <small>(1hr increments)</small></label>
                <select
                  id="endTime"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleInputChange}
                  required
                  disabled={!formData.startTime}
                >
                  <option value="">{formData.startTime ? 'Select duration...' : 'Select start time first'}</option>
                  {endTimeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="purpose">Purpose</label>
              <textarea
                id="purpose"
                name="purpose"
                value={formData.purpose}
                onChange={handleInputChange}
                placeholder="What is this booking for?"
                rows="3"
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={formSubmitting}
            >
              {formSubmitting ? 'Creating...' : 'Create Booking'}
            </button>
          </form>
        </section>
      </div>
    </Layout>
  );
};

export default EventSimpleDashboard;
